import { type ScreenType } from '@tmdb-graphql-api/schema/types';
import { PosterSize, ImageSize as imageType } from '#enums/imageSizes.ts';
import { SectionType } from '#enums/mediaTypes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { get } from '#helpers/otherHelpers.ts';
import { type Config } from '#types.ts';
import { type TileItem } from '../../Tile/types.ts';
import { type Credit, type CreditsResponse } from '../types.ts';
import { castExclusionList, crewExclusionList } from './exclusionLists.ts';
import { isCastCredit, isCrewCredit, isMovieCredit } from './isCreditType.ts';

const getTitle = (data: Credit) => (isMovieCredit(data) ? data.title : data.name);

const getSubTitle = (data: Credit) => {
  if ('character' in data && !!data.character) {
    return `as ${data.character}`;
  }

  if ('job' in data) {
    return data.job;
  }

  return;
};

const format = (credits: Credit[], { images }: Config) =>
  credits.reduce((acc: TileItem[], credit) => {
    const match = acc.find(entry => entry.title === getTitle(credit));

    if (match) {
      const subTitle = getSubTitle(credit);
      match.subTitle = [match.subTitle, subTitle].filter(entry => !!entry).join(' / ');
      return acc;
    }

    const { id, posterPath, voteAverage } = credit;

    acc.push({
      id,
      src: buildImagePath(posterPath, imageType.Poster, PosterSize.SM, images),
      subTitle: getSubTitle(credit),
      title: getTitle(credit),
      type: isMovieCredit(credit) ? SectionType.Movie : SectionType.Tv,
      voteAverage,
    });

    return acc;
  }, []);

export const formatCredits = <C extends Credit>(
  data: CreditsResponse | null | undefined,
  type: ScreenType,
  subTypeKey: 'cast' | 'crew',
  config: Config,
  filter?: (items: C[]) => C[],
) => {
  const credits = get<C[]>(data, `person.${type.toLowerCase()}Credits.${subTypeKey}`, []).filter(
    credit =>
      !(isCastCredit(credit, subTypeKey) && castExclusionList(credit)) &&
      !(isCrewCredit(credit, subTypeKey) && crewExclusionList(credit)),
  );

  const filteredCredits = filter ? filter(credits) : credits;
  return format(filteredCredits, config);
};
